import { useCallback } from 'react';
import { buildLogicContext } from '../../surveyLogic/context/buildLogicContext';
import { resolveSkipTarget } from '../../surveyLogic/evaluator/evaluateSkipLogics';
import { logOriginSkipTarget } from '../../surveyLogic/logger/logicLogger';
import { sanitizeHiddenQuestionAnswers } from '../../surveyLogic/navigation/sanitizeHiddenQuestionAnswers/index';
import { sanitizePagesAfterIndex } from '../../surveyLogic/navigation/sanitizeSkippedPageAnswers';
import { observeLogicMatchTransitions } from '../../surveyLogic/observers/observeLogicMatchTransitions';
import { trackSkipLogicMatched } from '../../surveyLogic/telemetry/logicMatchTelemetry';
import { earnQuestion } from '../../surveyProgress/utils/earnedProgress';
import {
  pathsEqual,
  pruneEarnedToActivePath,
  simulateActivePath,
} from '../../surveyProgress/utils/activePath';
import { QUESTION_TYPE } from '../../fetchSurvey/constants/questionTypes';
import { normalizeRankOrderAnswers } from '../../surveyLogic/fields/rankOrderUtils';
import {
  clearDeselectedMcqOtherText,
  getParentQuestionIdFromOptionLevelKey,
  seedSelectedMcqOtherText,
} from '../../surveyLogic/fields/mcqOtherUtils';
import type { McqQuestion } from '../../fetchSurvey/types/mcq';
import { isQuestionAnswered } from '../utils/validation';
import { logChangeAnswer } from '../logger/navigationLogger';
import type { SurveyPaginationContext } from '../types';
import type { AnswerValue, SurveyAnswers } from '../../submitSurvey/types';

function runLogicMatchObserver(
  context: SurveyPaginationContext,
  questionId: string,
  previousValues: SurveyAnswers,
  nextValues: SurveyAnswers
): void {
  const { survey, pages, paginationRefs, customFieldValues } = context;
  const refs = paginationRefs.current;
  if (!refs || !survey) return;

  const allQuestions = survey.pages.flatMap(page => page.questions);
  const previousContext = buildLogicContext(previousValues, allQuestions, customFieldValues);
  const nextContext = buildLogicContext(nextValues, allQuestions, customFieldValues);

  observeLogicMatchTransitions({
    survey,
    pages,
    allQuestions,
    previousValues,
    nextValues,
    customFieldValues,
    previousContext,
    nextContext,
    refs,
    triggerQuestionId: questionId,
  });
}

export function useOnChange(context: SurveyPaginationContext) {
  const {
    values,
    setValues,
    setValidationErrors,
    paginationRefs,
    bumpProgressRevision,
    pages,
    survey,
    currentPageIndex,
    customFieldValues,
  } = context;

  return useCallback(
    (questionId: string, value: AnswerValue) => {
      logChangeAnswer(questionId, value);

      const allQuestions = survey?.pages.flatMap(page => page.questions) ?? [];
      const parentQuestionId = getParentQuestionIdFromOptionLevelKey(questionId);
      const question = allQuestions.find(
        q => q.id === questionId || q.id === parentQuestionId
      );
      const storedValue =
        question?.type === QUESTION_TYPE.RANK_ORDER
          ? normalizeRankOrderAnswers(value)
          : value;

      let nextValues = { ...values, [questionId]: storedValue };

      if (question?.type === QUESTION_TYPE.MCQ && questionId === question.id) {
        const mcqQuestion = question as McqQuestion;
        nextValues = clearDeselectedMcqOtherText(mcqQuestion, nextValues, storedValue);
        nextValues = seedSelectedMcqOtherText(mcqQuestion, nextValues, storedValue);
      }

      const validationQuestionId = parentQuestionId ?? questionId;
      const refs = paginationRefs.current;
      let shouldBumpProgress = false;

      const applyLogicObserver = (resolvedValues: SurveyAnswers) => {
        runLogicMatchObserver(context, questionId, values, resolvedValues);
      };

      if (!refs || !survey) {
        setValues(nextValues);
        setValidationErrors(prev => ({ ...prev, [validationQuestionId]: '' }));
        return;
      }

      const previousPath = simulateActivePath(pages, values, allQuestions, customFieldValues);

      if (
        question?.required &&
        isQuestionAnswered(question, nextValues, allQuestions, customFieldValues)
      ) {
        earnQuestion(refs, questionId);
        shouldBumpProgress = true;
      }

      if (question?.skipLogics?.length) {
        const previousSkip = resolveSkipTarget(
          question,
          buildLogicContext(values, allQuestions, customFieldValues),
          pages,
          currentPageIndex,
          { silent: true }
        );
        const nextSkip = resolveSkipTarget(
          question,
          buildLogicContext(nextValues, allQuestions, customFieldValues),
          pages,
          currentPageIndex,
          { silent: true }
        );

        if (nextSkip && !previousSkip) {
          logOriginSkipTarget(question.id, nextSkip, currentPageIndex);
          refs.lastLoggedOriginSkipKey = `${currentPageIndex}:${nextSkip.skipLogicId}`;
          trackSkipLogicMatched(refs, {
            questionId: question.id,
            skipLogicId: nextSkip.skipLogicId,
            targetType: nextSkip.type === 'END_OF_SURVEY' ? 'END_OF_SURVEY' : 'QUESTION',
            targetId: nextSkip.type === 'END_OF_SURVEY' ? 'END_OF_SURVEY' : nextSkip.questionId,
            fromPageIndex: currentPageIndex,
            triggerQuestionId: questionId,
          });
        }

        const nextPath = simulateActivePath(pages, nextValues, allQuestions, customFieldValues);
        const pathChanged = !pathsEqual(previousPath, nextPath);
        pruneEarnedToActivePath(refs, pages, nextValues, allQuestions, customFieldValues);

        if (pathChanged) {
          for (let i = currentPageIndex + 1; i < pages.length; i += 1) {
            refs.skippedPageIndices.delete(i);
            refs.visitedPageIndices.delete(i);
          }
          const resolvedValues = sanitizePagesAfterIndex(pages, nextValues, currentPageIndex);
          applyLogicObserver(resolvedValues);
          setValues(resolvedValues);
          bumpProgressRevision();
          setValidationErrors(prev => ({ ...prev, [validationQuestionId]: '' }));
          return;
        }
      } else {
        const sanitized = sanitizeHiddenQuestionAnswers(
          pages,
          nextValues,
          allQuestions,
          customFieldValues
        );
        const nextPath = simulateActivePath(pages, sanitized, allQuestions, customFieldValues);
        const pathChanged = !pathsEqual(previousPath, nextPath);
        const pruned = pruneEarnedToActivePath(
          refs,
          pages,
          sanitized,
          allQuestions,
          customFieldValues
        );

        if (pathChanged) {
          for (let i = currentPageIndex + 1; i < pages.length; i += 1) {
            refs.skippedPageIndices.delete(i);
            refs.visitedPageIndices.delete(i);
          }
          const resolvedValues = sanitizePagesAfterIndex(pages, sanitized, currentPageIndex);
          applyLogicObserver(resolvedValues);
          setValues(resolvedValues);
          bumpProgressRevision();
          setValidationErrors(prev => ({ ...prev, [validationQuestionId]: '' }));
          return;
        }

        if (sanitized !== nextValues) {
          applyLogicObserver(sanitized);
          setValues(sanitized);
          if (pruned) {
            shouldBumpProgress = true;
          }
          setValidationErrors(prev => ({ ...prev, [validationQuestionId]: '' }));
          if (shouldBumpProgress) bumpProgressRevision();
          return;
        }

        if (pruned) {
          shouldBumpProgress = true;
        }
      }

      applyLogicObserver(nextValues);
      setValues(nextValues);
      if (shouldBumpProgress) bumpProgressRevision();

      setValidationErrors(prev => ({ ...prev, [validationQuestionId]: '' }));
    },
    [
      values,
      setValues,
      setValidationErrors,
      paginationRefs,
      bumpProgressRevision,
      pages,
      survey,
      currentPageIndex,
      customFieldValues,
      context,
    ]
  );
}
